/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/ban-types */
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  SelectProps,
} from "@material-ui/core";
import { FieldAttributes, useField } from "formik";
import * as React from "react";
import { useEffect, useState } from "react";
import { GroupedSelectData, SelectData } from "./selectData";
import { useScrollLockOnSelect } from "../../../hooks/useScrollLock";

/**
 * @interface CustomMultiSelectProps
 */
interface CustomMultiSelectProps {
  id: string;
  labelId: string;
  label: string;
  defaultOptions: string[] | number[];
  options: SelectData[] | GroupedSelectData[];
  defaultText?: string; // used for empty option
  isGrouped?: boolean;
  hasCustomChangeHandler?: boolean;
  customChangeHandler?: (values: string[] | number[]) => void;
  activateUseEffect?: boolean;
}

/**
 *
 * CustomMultiSelect
 *
 * A reusable Formik-connected MultiSelect component using Material-UI.
 * It supports internationalized option labels, validation error display,
 * and custom change handlers.
 *
 * @param {string} id - The id of the select element.
 * @param {string} labelId - The id of the label element.
 * @param {string} label - The label for the select element.
 * @param {string[]} defaultOptions - The default selected options.
 * @param {SelectData[] | GroupedSelectData[]} options - An array of options for the select element.
 * @param {string} defaultText - The text for the empty option.
 * @param {boolean} isGrouped - If true, the options are grouped.
 * @param {boolean} hasCustomChangeHandler - If true, a custom change handler is used.
 * @param {function} customChangeHandler - The custom change handler function.
 * @param {boolean} required - If true, the field is required.
 *
 * @returns {JSX.Element} The rendered CustomMultiSelect component.
 */
const CustomMultiSelect: React.FunctionComponent<
  FieldAttributes<{}> & CustomMultiSelectProps & SelectProps
> = ({
  defaultOptions,
  hasCustomChangeHandler,
  customChangeHandler,
  labelId,
  label,
  options,
  isGrouped,
  required,
  activateUseEffect = true,
  ...props
}) => {
  const [field, meta] = useField<{}>(props);
  const error = meta.error && meta.touched ? meta.error : "";
  const [optionName, setOptionName] = useState<string[] | number[]>(defaultOptions);
  const { handleOpen, handleClose } = useScrollLockOnSelect();

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    let value = event.target.value as string[];

    if (value.includes("none")) {
      value = []; // when '-' is selected, reset everything
    }
    setOptionName(value);
    if (hasCustomChangeHandler && customChangeHandler) {
      customChangeHandler(value);
    }
  };

  useEffect(() => {
    if (activateUseEffect) {
      setOptionName(defaultOptions);
    }
  }, [defaultOptions, activateUseEffect]);

  return (
    <FormControl fullWidth variant="filled" error={!!error}>
      <InputLabel required={required} id={labelId}>
        {label}
      </InputLabel>
      <Select
        {...field}
        {...props}
        multiple
        onOpen={handleOpen}
        onClose={handleClose}
        value={optionName}
        onChange={handleChange}
        MenuProps={{
          disableAutoFocus: true,
          disableScrollLock: true,
          getContentAnchorEl: null,
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "left",
          },
          MenuListProps: {
            onFocus: (e: React.FocusEvent) => e.preventDefault(),
          },
        }}
      >
        <MenuItem value="none">-</MenuItem>

        {isGrouped
          ? options &&
            (options as GroupedSelectData[]).map(
              (group: GroupedSelectData, index: number) => {
                return [
                  isGrouped &&
                    group.groupCategoryHeader !== "" &&
                    group.options.length > 0 && (
                      <ListSubheader
                        key={`sub-header-${group.groupCategoryHeader}`}
                        disableGutters={false}
                        disableSticky
                        color="default"
                        inset
                        style={{
                          width: "75%",
                          borderBottom: ".0625rem solid var(--secondary-accent-1)",
                          lineHeight: "0.1em",
                          margin: ".625rem 1rem 1.25rem",
                          fontSize: "1.25rem",
                        }}
                      >
                        <Box
                          component="span"
                          style={{
                            background: "#fff",
                            padding: "0 .625rem",
                          }}
                        >
                          {group.groupCategoryHeader}
                        </Box>
                      </ListSubheader>
                    ),
                  group.options.map((option: SelectData, j: number) => (
                    <MenuItem key={option.key} value={option.value}>
                      {option.label ? option.label : option.value}
                    </MenuItem>
                  )),
                ];
              }
            )
          : options &&
            (options as SelectData[]).map((option: SelectData, j: number) => (
              <MenuItem key={option.key} value={option.value}>
                {option.label ? option.label : option.value}
              </MenuItem>
            ))}
      </Select>
      <FormHelperText>{error}</FormHelperText>
    </FormControl>
  );
};

export default CustomMultiSelect;
